import React, {Fragment} from 'react';
import {Col, Container, Row} from 'reactstrap';
import ReactMarkdown from 'react-markdown';
import classNames from 'classnames';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import Header from 'components/Header';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import {LandingSpeakerList} from 'components/SpeakerList';
import LandingTimerSection from 'components/Landing/LandingTimerSection';
import ScrollingVideo from 'components/ComponentLibrary/ScrollingVideo';
import IconListCL from 'components/ComponentLibrary/IconListCL/IconListCL';
import CountdownTimer from 'components/Elements/CountdownTimer';
import OptinModal, {OptinModalTrigger} from 'components/OptinModal';
import ModalExitLanding from 'components/Landing/ModalExitLanding';
import Footer from 'components/Footer';

export default function ({
  entry = 'default',
  variant = 'default',
  noExitIntent = false,
  noFixedVideo = false,
  pageTitle = 'Join the FREE, Online WHOLE Life Action Hour Series',
  pageDescription = 'Get trusted solutions and practical, science-based action steps you can take NOW to achieve the lasting health and vitality you deserve.',
  shareUrl = 'https://wlc.foodrevolution.org/',
  shareImage = 'https://cdn.foodrevolution.org/wlc/opengraph/WLC-opengraph-landing-1200x1200-202205.png',
  ...props
}) {
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        currentActionHour: 0,
        actionHours: [
          {
            date: '',
            description: '',
            speaker: [{name: '', shortName: '', bio: '', image: '', url: ''}],
            title: '',
            video: ''
          }
        ]
      }
    }
  } = usePhaseState();

  const isSeries = defaultSchedule.actionHours.length > 1;
  const seriesName = `Action Hour${isSeries ? ' Series' : ''}`;
  const currentActionHour = defaultSchedule.currentActionHour;
  const beforeActionHour = defaultSchedule.phase === 'before';
  const duringActionHour = defaultSchedule.phase === 'during';

  const actionHourSpeaker = defaultSchedule.actionHours[currentActionHour].speaker;

  const isSpecialLaunch = defaultSchedule.specialLaunch.isActive;
  const specialLaunchData = defaultSchedule.specialLaunch;
  const timerEnd = new Date(defaultSchedule.actionHours[defaultSchedule.currentActionHour].date).getTime();

  const videoUrl = isSpecialLaunch
    ? defaultSchedule.specialLaunch.landingVideo
    : isSeries
    ? 'https://www.youtube.com/watch?v=sRJj4T_7I-MM'
    : 'https://www.youtube.com/watch?v=rbVEhqCwWEs';

  const ctaButtonCopy = 'Join Now';

  function CountdownSection({className = undefined}) {
    return (
      <div className={classNames(`section-cta-countdown`, className)}>
        {isSpecialLaunch ? (
          <>
            {beforeActionHour ? (
              <div className="timer">
                <h4 className="timer-heading">The Action Hour Starts In:</h4>
                <CountdownTimer time={timerEnd} />
              </div>
            ) : duringActionHour ? (
              <h4 className="section-only-heading section-countdown-heading-demand text-uppercase">
                The Action Hour is Live Now
              </h4>
            ) : (
              <h4 className="section-only-heading section-countdown-heading-demand text-uppercase">
                The Action Hour is on Replay
              </h4>
            )}
          </>
        ) : (
          <LandingTimerSection className="section-countdown-cta" />
        )}
      </div>
    );
  }

  useBalanceText();
  return (
    <div className="page">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={shareImage} />
      <div className="page-content page-nosocial">
        <Header bgColor="black" lockupWhite isSeries={isSeries} adLockup={variant === 'ad' ? true : false} />
        <SectionCL id="landing-header" color="purple" innerClass="text-center text-white" order="first" type="triangle">
          <Container>
            <Row className="d-flex justify-content-center">
              <Col xs="12">
                <p className="mb-4">
                  <i>Free</i> {isSeries ? 'Action Hour Interview Series' : 'Action Hour Interview'} Hosted By Food
                  Revolution Network CEO Ocean Robbins
                </p>
              </Col>
              <Col xs="12" lg="10" className="video-col px-0 px-lg-3 mb-4">
                <ScrollingVideo
                  bgColor="purple"
                  video={{url: videoUrl, label: `WLC-AH - Landing`}}
                  text={<p className="text-h4">Join in for the WHOLE Life Action Hour</p>}
                  button={{
                    optin: true,
                    text: ctaButtonCopy
                  }}
                />
              </Col>
              <Col xs="12" lg="10">
                <h3 className="section-heading text-400">
                  {isSpecialLaunch ? (
                    specialLaunchData.landingHeadline
                  ) : (
                    <>
                      Why Most People Will Suffer From Pain, <br className="d-none d-xl-block" />
                      Brain Fog, and Chronic Disease as They <br className="d-none d-xl-block" />
                      Age — and <br className="d-none d-sm-block d-md-none" />
                      How to Beat Those Odds
                    </>
                  )}
                </h3>
                <h6 className="mt-0 mb-4">
                  {isSpecialLaunch ? (
                    specialLaunchData.landingSubHeadline
                  ) : (
                    <>
                      Get {isSeries ? <>Behind the Scenes Interviews</> : <>a Behind the Scenes Interview</>} with a Top
                      Health Expert
                    </>
                  )}
                </h6>
                <p className="mb-4">Join the WHOLE Life {seriesName}</p>
                <OptinModalTrigger btnText={ctaButtonCopy} />
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="landing-presents" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center">
                  Food Revolution Network is thrilled to bring you this WHOLE Life {seriesName}
                </h2>
                <h4 className="mt-0 text-center">
                  Featuring Top Strategies for a Lifetime of Feeling Your Absolute Best
                </h4>
              </Col>
            </Row>
            <LandingSpeakerList teaser />
            <Row className="justify-content-center align-items-center">
              <Col xs="10" md="6" lg="5" className="text-center mb-4 mb-lg-0">
                <img
                  className="img-speaker-ocean rounded-circle"
                  src="https://cdn.foodrevolution.org/global/team/frn-guest_expert-portrait-square-ocean_robbins.jpg"
                />
              </Col>
              <Col lg="7">
                <h3 className="section-heading text-center text-lg-left mb-0">Ocean Robbins</h3>
                <h4 className="mt-1 text-400 text-center text-lg-left">Your Host</h4>
                <p>
                  Ocean Robbins, co-founder and CEO of the million-member Food Revolution Network, is a TEDx speaker,
                  best-selling author, husband, father, gardener, and dancer. Born in a log cabin and raised on
                  home-grown food, his work is rooted in his family’s health-conscious legacy. From founding the
                  nonprofit YES! at age 16 to leading a global food revolution movement, Ocean is on a mission to
                  transform our food system, drive down the chronic disease epidemic, and mobilize people worldwide to
                  be part of the solution for a healthier planet and population. His books include{' '}
                  <i>31-Day Food Revolution</i> and <i>Powered By Plants.</i>
                </p>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL
          id="landing-discover-one"
          color="white"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/1136640691-square.jpg"
          imgDescription="woman with white curly hair with green smoothie"
          imgPosition="right"
        >
          <h2 className="section-heading">
            {isSpecialLaunch ? (
              specialLaunchData.landingAHListHeadline
            ) : (
              <>
                {
                  {
                    default: <>In Each Inspiring Action Hour, You’ll Discover:</>,
                    ad: <>What’s Inside Each Inspiring Action Hour:</>
                  }[variant]
                }
              </>
            )}
          </h2>
          {isSpecialLaunch ? (
            <>
              <IconListCL>
                {specialLaunchData.landingAHList.map((item, i) => {
                  return <ReactMarkdown className="md-lc-0" key={`listItem` + i} source={item} />;
                })}
              </IconListCL>
            </>
          ) : (
            <IconListCL>
              <>
                {
                  {
                    default: (
                      <>
                        <b>
                          Why FOOD is the strongest — and most delicious — ally in the fight against chronic illness.
                        </b>{' '}
                        Get ready to learn the top superfoods that are right in your produce aisle.
                      </>
                    ),
                    ad: (
                      <>
                        <b>
                          Why FOOD is the strongest — and most delicious — ally in the fight against chronic illness.
                        </b>{' '}
                        Get ready to learn the top superfoods that are right in the produce aisle.
                      </>
                    )
                  }[variant]
                }
              </>
              <>
                <b>
                  The scientifically proven steps to regain the ENERGY, JOY, and PEACE that’s possible…at any age and
                  stage of life!
                </b>{' '}
                With the right tips, feeling good again comes naturally.
              </>
              <>
                {
                  {
                    default: (
                      <>
                        <b>How to optimize your immune system to support a healthy response to life’s challenges.</b>{' '}
                        From colds and viruses to cancer prevention: discover what you need beyond vitamin C!
                      </>
                    ),
                    ad: (
                      <>
                        <b>How to optimize the immune system to support a healthy response to life’s challenges.</b>{' '}
                        This goes way more in-depth than vitamin C!
                      </>
                    )
                  }[variant]
                }
              </>
              <>
                <b>New healthy, mouth-watering, plant-powered recipes that are easy to make.</b> Be sure to watch each
                Action Hour for a new nutrient-rich recipe to devour right away.
              </>
            </IconListCL>
          )}
        </SectionCL>

        <SectionCL
          id="landing-discover-two"
          color="light-gray"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/627315408-square.jpg"
          imgDescription="man and woman looking at tablet sitting in an outdoor cafe"
          imgPosition="left"
        >
          <h4 className="section-heading mb-4">
            {
              {
                default: <>PLUS, during the Action Hour, learn how to join WHOLE Life Club.</>,
                ad: <>PLUS, during the Action Hour, we’ll share how to join WHOLE Life Club.</>
              }[variant]
            }
          </h4>
          <p>Live the healthiest life possible. Enjoy lasting RESULTS.</p>
          <p>
            Find out why members call WHOLE Life Club “a lifeline” and get an invite to lock in huge savings during the
            Action Hour.
          </p>
        </SectionCL>

        <SectionCL
          id="landing-cta-1"
          color="purple"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/530755744-square.jpg"
          imgDescription="woman in the kitchen with a pineapple on cutting board"
          imgPosition="right"
          innerClass="text-white text-center"
        >
          <h3 className="section-heading">Join the WHOLE Life {seriesName} Today</h3>
          <p>
            <i>
              {
                {
                  default: (
                    <>Because you deserve a smart immune system, a healthy body, a clear mind, and lasting vitality.</>
                  ),
                  ad: (
                    <>
                      Because we ALL deserve a smart immune system, a healthy body, a clear mind, and lasting vitality.
                    </>
                  )
                }[variant]
              }
            </i>
          </p>
          <OptinModalTrigger btnText={ctaButtonCopy} />
          <CountdownSection className="mt-4" />
        </SectionCL>

        <SectionCL id="landing-thriving" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-center">
                  {
                    {
                      default: (
                        <>
                          {isSeries ? 'Each' : 'This'} WHOLE Life Action Hour Features Information You Can Trust —{' '}
                          <i>Without</i> Hype
                        </>
                      ),
                      ad: (
                        <>
                          {isSeries ? 'Each' : 'This'} WHOLE Life Action Hour Features Trustworthy Information —{' '}
                          <i>Without</i> Hype
                        </>
                      )
                    }[variant]
                  }
                </h3>
              </Col>
            </Row>
            <Row className="d-flex justify-content-center">
              <Col lg="10">
                <p>
                  <b>
                    {
                      {
                        default: <>Our experts are world-leading food and health mentors — with integrity and heart.</>,
                        ad: (
                          <>
                            Our experts are vetted as top, world-leading food and health mentors — with integrity and
                            heart.
                          </>
                        )
                      }[variant]
                    }
                  </b>
                </p>
                <p>
                  At Food Revolution Network, we ♥️ science, and we go to great lengths to make sure that the resources
                  we share are based on peer-reviewed research, scholarly articles, university studies, and other
                  credible sources.
                </p>
                <p>
                  {
                    {
                      default: (
                        <>
                          We’re critical thinkers with great big hearts, and we know you are, too! That’s why this{' '}
                          {seriesName} is your trusted source for food and lifestyle wisdom, recipes, and action steps
                          that make a difference in how you feel every day.
                        </>
                      ),
                      ad: (
                        <>
                          That’s why this {seriesName} is the trusted source for food and lifestyle wisdom, recipes, and
                          action steps to take right away.
                        </>
                      )
                    }[variant]
                  }
                </p>
                <IconListCL>
                  <>
                    {
                      {
                        default: (
                          <>
                            <b>We don’t do “fake news.”</b> Every guest we interview is an expert in their field. You
                            need the best information to create a healthy life, and we’re determined to make sure that
                            what you learn is actionable, results-oriented, and trustworthy.
                          </>
                        ),
                        ad: (
                          <>
                            <b>We don’t do “fake news.”</b> Every guest we interview is an expert in their field. What
                            we’re sharing is results-oriented, actionable, and trustworthy.
                          </>
                        )
                      }[variant]
                    }
                  </>
                  <>
                    {
                      {
                        default: (
                          <>
                            <b>We focus on your WHOLE self.</b> Everything is connected, so we focus on healthy food
                            habits, meaningful family traditions, and wise lifestyle choices that anyone can adopt.
                          </>
                        ),
                        ad: (
                          <>
                            <b>We focus on the WHOLE self.</b> Everything is connected, so we focus on healthy food
                            habits, meaningful family traditions, and wise lifestyle choices that anyone can adopt.
                          </>
                        )
                      }[variant]
                    }
                  </>
                  <>
                    {
                      {
                        default: (
                          <>
                            <b>These leaders take you to the next level: naturally!</b> Get insights to improve on every
                            level: mind, body, and spirit. We bring holistic wisdom to help you feel supported with
                            kindness, ethics, consciousness, and LOVE.
                          </>
                        ),
                        ad: (
                          <>
                            <b>These leaders take things to the next level: naturally!</b> Get insights to improve on
                            every level: mind, body, and spirit. We bring holistic wisdom with kindness, ethics,
                            consciousness, and LOVE.
                          </>
                        )
                      }[variant]
                    }
                  </>
                </IconListCL>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL
          id="landing-cta-2"
          color="purple"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/1163965536-square.jpg"
          imgDescription="woman grabbing vegetable from inside fridge"
          imgPosition="right"
          className="text-white text-center"
          angleBottomDisplay="none"
          angle="none"
          order="last"
        >
          <h3 className="section-heading">
            {
              {
                default: (
                  <>
                    Sign up now for the WHOLE Life {seriesName} and Discover Simple Steps to Health that Lasts a
                    Lifetime
                  </>
                ),
                ad: <>Sign up now for the WHOLE Life {seriesName} for Simple Steps to Health that Lasts a Lifetime</>
              }[variant]
            }
          </h3>
          <OptinModalTrigger btnText={ctaButtonCopy} />
          <CountdownSection className="mt-4" />
        </SectionCL>
      </div>

      <Footer adVariant={variant === 'ad' ? true : false} />
      <OptinModal
        entry={entry}
        contactInfoFormHeadline={
          <>
            <h5 className="section-heading text-center">Register for the {seriesName} now — for free!</h5>
            <p className="text-center">
              Enter your name and email below to claim your spot in the WHOLE Life {seriesName} and get access to these
              game-changing events — for FREE.
            </p>
          </>
        }
      />
      {!noExitIntent && <ModalExitLanding launch={isSeries} />}
    </div>
  );
}
